/**
 * timeline.js — the strip where the takes are moved in time.
 *
 * A clone dialogue lives or dies on WHEN each take speaks, so the timeline is
 * not decoration here — it is the tool's second half. Two decisions shape it:
 *
 *   • Speech is DRAWN. Each bar carries the take's detected speech spans, so
 *     "these two are talking over each other" is something you see instead of
 *     something you discover on playback. This is the same measurement the
 *     arrange-turns button uses, so the picture and the button can never
 *     disagree.
 *   • It is one canvas, not a hundred divs. Rows re-render on every drag and on
 *     every playhead tick; DOM churn at that rate is what makes editor strips
 *     feel cheap.
 *
 * Interaction: drag a bar to move the take, drag its ends to trim, drag or
 * click the ruler to scrub. Pointer coordinates are converted through the LIVE
 * element rect every time — a canvas laid out by CSS is a different size from
 * its bitmap, and caching that ratio is how hit-testing quietly drifts.
 *
 * Exposes window.CloneTimeline.
 */
(function () {
'use strict';
if (window.CloneTimeline) return;

var ROW_H = 44;
var RULER_H = 22;
var PAD = 10;
var HANDLE = 9;          // px grab zone on a bar's ends

function Timeline(canvas, project, opts) {
    this.cv = canvas;
    this.ctx = canvas.getContext('2d');
    this.p = project;
    this.opts = opts || {};
    this.t = 0;
    this.drag = null;
    this.hover = null;
    this.spans = {};         // clipIndex → [{start,end}] in SOURCE time
    this.musicSpans = null;
    this.selected = 0;

    this._bind();
}

Timeline.prototype.rows = function () {
    var rows = [];
    for (var i = 0; i < this.p.clips.length; i++) {
        if (this.p.clips[i]) rows.push({ kind: 'clip', index: i });
    }
    if (this.p.music) rows.push({ kind: 'music', index: -1 });
    return rows;
};

Timeline.prototype.height = function () {
    return RULER_H + this.rows().length * ROW_H + PAD;
};

/* Visible span: always a bit longer than the content so a take can be dragged
   past the current end without the strip refusing to show where it went. */
Timeline.prototype.span = function () {
    var d = window.CloneScene.projectDuration(this.p);
    return Math.max(4, d * 1.08 + 0.6);
};

Timeline.prototype.x2t = function (x) {
    var w = this.cv.clientWidth - PAD * 2;
    return Math.max(0, (x - PAD) / Math.max(1, w) * this.span());
};
Timeline.prototype.t2x = function (t) {
    var w = this.cv.clientWidth - PAD * 2;
    return PAD + t / this.span() * w;
};

Timeline.prototype.setTime = function (t) { this.t = t; this.render(); };
Timeline.prototype.setSpans = function (clipIndex, spans) { this.spans[clipIndex] = spans; this.render(); };
Timeline.prototype.select = function (i) { this.selected = i; this.render(); };

/* ─────────────────────────────── render ─────────────────────────────── */

Timeline.prototype.render = function () {
    var cv = this.cv, ctx = this.ctx;
    var cssW = cv.clientWidth || 600;
    var cssH = this.height();
    var dpr = Math.min(2, window.devicePixelRatio || 1);

    if (cv.width !== Math.round(cssW * dpr) || cv.height !== Math.round(cssH * dpr)) {
        cv.width = Math.round(cssW * dpr);
        cv.height = Math.round(cssH * dpr);
        cv.style.height = cssH + 'px';
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // A canvas inherits the document's direction, and with `direction: rtl` the
    // DEFAULT textAlign ('start') anchors on the RIGHT. Every label here is
    // positioned by its left edge inside a clip rect, so under Arabic the text
    // was drawn leaving that rect and vanished — a bar with no name on it.
    // The strip itself stays left-to-right in every locale (media timelines do,
    // the canvas is not mirrored by CSS, and mirroring only the labels would
    // put them on the wrong bar).
    ctx.direction = 'ltr';
    ctx.textAlign = 'left';

    var css = getComputedStyle(document.documentElement);
    var col = {
        line: (css.getPropertyValue('--border') || '#333').trim(),
        muted: (css.getPropertyValue('--text-muted') || '#888').trim(),
        text: (css.getPropertyValue('--text-secondary') || '#ccc').trim(),
        accent: (css.getPropertyValue('--accent') || '#6366f1').trim(),
        panel: (css.getPropertyValue('--bg-tertiary') || '#222').trim()
    };

    this._ruler(ctx, cssW, col);

    var rows = this.rows();
    for (var r = 0; r < rows.length; r++) {
        var y = RULER_H + r * ROW_H;
        if (rows[r].kind === 'clip') this._clipRow(ctx, rows[r].index, y, col, cssW);
        else this._musicRow(ctx, y, col, cssW);
    }

    // Playhead last so it is never hidden behind a bar.
    var px = this.t2x(this.t);
    ctx.strokeStyle = col.accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, 2); ctx.lineTo(px, cssH - 4);
    ctx.stroke();
    ctx.fillStyle = col.accent;
    ctx.beginPath();
    ctx.moveTo(px - 5, 2); ctx.lineTo(px + 5, 2); ctx.lineTo(px, 9);
    ctx.closePath(); ctx.fill();
};

Timeline.prototype._ruler = function (ctx, w, col) {
    var span = this.span();
    // Pick a tick that leaves ~70 px between labels at any zoom.
    var steps = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];
    var pxPerSec = (w - PAD * 2) / span;
    var step = steps[steps.length - 1];
    for (var i = 0; i < steps.length; i++) { if (steps[i] * pxPerSec >= 62) { step = steps[i]; break; } }

    ctx.font = '10px system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillStyle = col.muted;
    ctx.strokeStyle = col.line;
    ctx.lineWidth = 1;
    for (var t = 0; t <= span; t += step) {
        var x = Math.round(this.t2x(t)) + 0.5;
        ctx.beginPath();
        ctx.moveTo(x, RULER_H - 6); ctx.lineTo(x, RULER_H - 1);
        ctx.stroke();
        ctx.fillText(fmt(t), x + 3, 2);
    }
};

Timeline.prototype._clipRow = function (ctx, i, y, col, w) {
    var c = this.p.clips[i];
    var CS = window.CloneScene;
    var dur = CS.clipDuration(c);
    var x0 = this.t2x(c.start), x1 = this.t2x(c.start + dur);
    var bh = ROW_H - 12;
    var by = y + 5;
    var sel = this.selected === i;

    ctx.save();
    ctx.beginPath();
    rr(ctx, x0, by, Math.max(6, x1 - x0), bh, 6);
    ctx.fillStyle = sel ? hexA(col.accent, 0.26) : hexA(col.accent, 0.13);
    ctx.fill();
    ctx.strokeStyle = sel ? col.accent : col.line;
    ctx.lineWidth = sel ? 1.6 : 1;
    ctx.stroke();
    ctx.clip();

    // Speech spans — the reason this strip exists.
    var spans = this.spans[i];
    if (spans && spans.length) {
        ctx.fillStyle = hexA(col.accent, 0.55);
        for (var s = 0; s < spans.length; s++) {
            var sp = spans[s];
            // Source time → composition time, honouring the clip's trim.
            var a = c.start + (sp.start - c.trimIn);
            var b = c.start + (sp.end - c.trimIn);
            if (b < c.start || a > c.start + dur) continue;
            a = Math.max(a, c.start); b = Math.min(b, c.start + dur);
            var ax = this.t2x(a), bx = this.t2x(b);
            ctx.fillRect(ax, by + bh - 9, Math.max(2, bx - ax), 6);
        }
    }
    if (c.muted) {
        ctx.fillStyle = hexA(col.muted, 0.30);
        ctx.fillRect(x0, by, x1 - x0, bh);
    }
    ctx.restore();

    ctx.fillStyle = col.text;
    ctx.font = '11.5px system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    var label = (c.muted ? '🔇 ' : '') + (this.opts.clipLabel ? this.opts.clipLabel(i) : ('#' + (i + 1)));
    ctx.save();
    ctx.beginPath(); ctx.rect(x0 + 4, by, Math.max(0, x1 - x0 - 8), bh); ctx.clip();
    ctx.fillText(label, x0 + 8, by + bh / 2 - 3);
    ctx.restore();
};

Timeline.prototype._musicRow = function (ctx, y, col, w) {
    var m = this.p.music;
    var CS = window.CloneScene;
    var total = CS.projectDuration(this.p);
    var dur = CS.musicDuration(m);
    var end = m.loop ? total : Math.min(total, m.start + dur);
    var x0 = this.t2x(m.start), x1 = this.t2x(end);
    var bh = ROW_H - 12, by = y + 5;

    ctx.beginPath();
    rr(ctx, x0, by, Math.max(6, x1 - x0), bh, 6);
    ctx.fillStyle = hexA(col.muted, 0.16);
    ctx.fill();
    ctx.strokeStyle = col.line;
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = col.text;
    ctx.font = '11.5px system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.save();
    ctx.beginPath(); ctx.rect(x0 + 4, by, Math.max(0, x1 - x0 - 8), bh); ctx.clip();
    ctx.fillText('🎵 ' + (this.opts.musicLabel || m.name || ''), x0 + 8, by + bh / 2);
    ctx.restore();
};

/* ───────────────────────────── interaction ───────────────────────────── */

Timeline.prototype._hit = function (x, y) {
    if (y < RULER_H) return { kind: 'scrub' };
    var rows = this.rows();
    var r = Math.floor((y - RULER_H) / ROW_H);
    if (r < 0 || r >= rows.length) return { kind: 'scrub' };
    var row = rows[r];

    if (row.kind === 'music') {
        var m = this.p.music;
        var mx0 = this.t2x(m.start);
        var mx1 = this.t2x(m.start + window.CloneScene.musicDuration(m));
        if (x >= mx0 - 4 && x <= mx1 + 4) return { kind: 'music-move' };
        return { kind: 'scrub' };
    }

    var i = row.index, c = this.p.clips[i];
    var dur = window.CloneScene.clipDuration(c);
    var x0 = this.t2x(c.start), x1 = this.t2x(c.start + dur);
    if (x < x0 - 4 || x > x1 + 4) return { kind: 'scrub' };
    if (x <= x0 + HANDLE) return { kind: 'trim-in', index: i };
    if (x >= x1 - HANDLE) return { kind: 'trim-out', index: i };
    return { kind: 'move', index: i };
};

Timeline.prototype._bind = function () {
    var self = this;

    function local(e) {
        // LIVE rect every time: the strip is responsive and a cached ratio
        // sends every drag to the wrong second after one window resize.
        var r = self.cv.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    this.cv.addEventListener('pointerdown', function (e) {
        var pt = local(e);
        var hit = self._hit(pt.x, pt.y);
        self.cv.setPointerCapture(e.pointerId);

        if (hit.kind === 'scrub') {
            self.drag = { kind: 'scrub' };
            self.opts.onScrub && self.opts.onScrub(self.x2t(pt.x));
        } else {
            var c = hit.index != null ? self.p.clips[hit.index] : null;
            self.drag = {
                kind: hit.kind, index: hit.index, grabT: self.x2t(pt.x),
                start0: c ? c.start : (self.p.music ? self.p.music.start : 0),
                in0: c ? c.trimIn : 0, out0: c ? c.trimOut : 0
            };
            if (hit.index != null) {
                self.selected = hit.index;
                self.opts.onSelect && self.opts.onSelect(hit.index);
            }
        }
        self.render();
        e.preventDefault();
    });

    this.cv.addEventListener('pointermove', function (e) {
        var pt = local(e);
        if (!self.drag) {
            var h = self._hit(pt.x, pt.y);
            var cur = h.kind === 'trim-in' || h.kind === 'trim-out' ? 'ew-resize'
                : (h.kind === 'move' || h.kind === 'music-move') ? 'grab' : 'pointer';
            if (self.cv.style.cursor !== cur) self.cv.style.cursor = cur;
            return;
        }
        var t = self.x2t(pt.x);
        var d = self.drag;

        if (d.kind === 'scrub') { self.opts.onScrub && self.opts.onScrub(t); return; }

        var delta = t - d.grabT;
        if (d.kind === 'move') {
            self.p.clips[d.index].start = Math.max(0, snap(d.start0 + delta, self, d.index));
        } else if (d.kind === 'music-move') {
            self.p.music.start = Math.max(0, d.start0 + delta);
        } else if (d.kind === 'trim-in') {
            var c1 = self.p.clips[d.index];
            var ni = Math.max(0, Math.min(d.in0 + delta, (c1.trimOut || c1.duration) - 0.2));
            // Trimming the head must not slide the clip: the frame under the
            // cursor stays put and only the material before it is discarded.
            c1.start = Math.max(0, d.start0 + (ni - d.in0));
            c1.trimIn = ni;
        } else if (d.kind === 'trim-out') {
            var c2 = self.p.clips[d.index];
            c2.trimOut = Math.max(c2.trimIn + 0.2, Math.min(d.out0 + delta, c2.duration));
        }
        self.opts.onChange && self.opts.onChange();
        self.render();
    });

    function end(e) {
        if (!self.drag) return;
        try { self.cv.releasePointerCapture(e.pointerId); } catch (_) {}
        var wasEdit = self.drag.kind !== 'scrub';
        self.drag = null;
        if (wasEdit) self.opts.onCommit && self.opts.onCommit();
        self.render();
    }
    this.cv.addEventListener('pointerup', end);
    this.cv.addEventListener('pointercancel', end);

    window.addEventListener('resize', function () { self.render(); });
};

/* Snap a dragged take to the edges of the others and to zero — clone dialogue
   is built out of "starts where that one ends", and hitting that by hand at
   0.03 s precision is busywork. */
function snap(v, tl, skipIndex) {
    var CS = window.CloneScene;
    var tolPx = 7;
    var tol = tolPx / Math.max(1, (tl.cv.clientWidth - PAD * 2)) * tl.span();
    var cands = [0];
    for (var i = 0; i < tl.p.clips.length; i++) {
        if (i === skipIndex) continue;
        var c = tl.p.clips[i];
        if (!c) continue;
        cands.push(c.start, c.start + CS.clipDuration(c));
    }
    for (var k = 0; k < cands.length; k++) {
        if (Math.abs(v - cands[k]) < tol) return cands[k];
    }
    return v;
}

/* ─────────────────────────────── helpers ─────────────────────────────── */

function rr(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function hexA(hex, a) {
    hex = (hex || '').trim();
    if (hex[0] !== '#') return 'rgba(99,102,241,' + a + ')';
    if (hex.length === 4) hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    var r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
}

function fmt(t) {
    var m = Math.floor(t / 60), s = t - m * 60;
    return m + ':' + (s < 10 ? '0' : '') + (s % 1 ? s.toFixed(1) : s.toFixed(0));
}

window.CloneTimeline = { Timeline: Timeline, ROW_H: ROW_H, RULER_H: RULER_H, fmt: fmt };
})();
